import React, { useEffect, useState } from "react";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import MainModal from "components/Modal/MainModal";
import { toast } from "react-toastify";
import { adminApiService } from "../../../services/api";

const MovingText = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ text: "" });
  const [texts, setTexts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingText, setEditingText] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchTexts = async () => {
    setIsLoading(true);
    try {
      const res = await adminApiService.getMovingTexts();
      if (!res.ok) throw new Error("Failed to fetch texts");
      const { data } = await res.json();
      setTexts(data);
    } catch (error) {
      toast.error("Error fetching moving texts.");
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTexts();
  }, []);

  const handleDelete = async (text) => {
    if (!window.confirm(`Are you sure you want to delete this text?`)) {
      return;
    }

    try {
      const res = await adminApiService.deleteMovingText(text.id);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to delete text");
      }

      toast.success("Moving text deleted successfully!");
      await fetchTexts();
    } catch (error) {
      toast.error(`Error deleting moving text: ${error.message}`);
      console.error("Delete error:", error);
    }
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingText(null);
    setFormData({ text: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (text) => {
    setIsEditMode(true);
    setEditingText(text);
    setFormData({ text: text.text });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.text.trim()) {
      toast.error("Please enter the moving text.");
      return;
    }

    try {
      const payload = {
        text: formData.text.trim(),
        ...(isEditMode && { id: editingText.id }),
      };

      const res = isEditMode
        ? await adminApiService.updateMovingText(payload)
        : await adminApiService.addMovingText(payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.message || `Failed to ${isEditMode ? "update" : "add"} text`
        );
      }

      toast.success(
        `Moving text ${isEditMode ? "updated" : "added"} successfully!`
      );
      await fetchTexts();
      setIsModalOpen(false);
      setFormData({ text: "" });
      setIsEditMode(false);
      setEditingText(null);
    } catch (error) {
      toast.error(
        `Error ${isEditMode ? "updating" : "submitting"} moving text: ${
          error.message
        }`
      );
      console.error("Submit error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-10 rounded-2xl bg-white p-8 shadow-sm">
          <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                Moving Text Management
              </h1>
              <p className="mt-3 text-base text-gray-600">
                Manage the moving texts displayed on the website
              </p>
            </div>
            <button
              onClick={openAddModal}
              className="inline-flex items-center rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primaryDark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <MdAdd className="mr-2" size={24} />
              Add New Text
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="mt-8">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="border-t-transparent h-12 w-12 animate-spin rounded-full border-4 border-blue-600"></div>
            </div>
          ) : texts.length === 0 ? (
            <div className="flex h-96 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
              <div className="rounded-full bg-blue-50 p-6">
                <MdAdd className="h-16 w-16 text-primary" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-gray-900">
                No Moving Texts
              </h3>
              <p className="mt-3 text-base text-gray-500">
                Get started by adding your first moving text to display on the
                website.
              </p>
              <button
                onClick={openAddModal}
                className="mt-8 inline-flex items-center rounded-xl bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-all hover:bg-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <MdAdd className="mr-2" size={24} />
                Add New Text
              </button>
            </div>
          ) : (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {texts.map((text) => (
                <div
                  key={text.id}
                  className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-lg"
                >
                  <div className="flex flex-col">
                    <p className="text-lg font-medium text-gray-900">
                      {text.text}
                    </p>
                    <div className="mt-6 flex items-center justify-end space-x-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      <button
                        onClick={() => openEditModal(text)}
                        className="rounded-xl p-2.5 text-primary transition-colors hover:bg-blue-50"
                        aria-label="Edit text"
                      >
                        <MdEdit size={24} />
                      </button>
                      <button
                        onClick={() => handleDelete(text)}
                        className="rounded-xl p-2.5 text-red-600 transition-colors hover:bg-red-50"
                        aria-label="Delete text"
                      >
                        <MdDelete size={24} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <MainModal
        isOpen={isModalOpen}
        setModalOpen={setIsModalOpen}
        title={isEditMode ? "Edit Moving Text" : "Add New Moving Text"}
        btnTitle={isEditMode ? "Update Text" : "Add Text"}
        actionbtn={handleSubmit}
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="text"
              className="mb-3 block text-base font-medium text-gray-700"
            >
              Text *
            </label>
            <textarea
              id="text"
              name="text"
              value={formData.text}
              onChange={handleInputChange}
              rows={4}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="Enter the moving text..."
            />
            <p className="mt-3 text-sm text-gray-500">
              This text will be displayed as a moving text on the website.
            </p>
          </div>
        </div>
      </MainModal>
    </div>
  );
};

export default MovingText;
